import { useMemo } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { WALLBOARD_WIDGETS, CAMPAIGN_WIDGETS, type LiveQueue, type WallboardLayout, type WallboardWidget } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { formatMoney } from '../../lib/format.js';
import { Icon, type IconName } from '../../components/ui/Icon.js';
import { useShiftsChannel } from '../../lib/api/realtime.js';
import { useLiveState } from './useLiveState.js';
import { TONE, mmss, worst, queueSlug, type Tone } from './wallboardShared.js';

/**
 * Wallboard widgets and the data they draw from (owner, 26 September 2026). A board -- saved
 * (0134) or one of the two ready-made ones -- is a list of these on a four-column grid.
 *
 *   live       the queues channel: waiting and longest wait now, agents free and bridged
 *   queues     GET /wallboard: every queue with today's counts, for names and queue tiles
 *   detail     GET /wallboard/queue per queue a widget needs: today's figures, members and
 *              their state, calls by hour. "Every queue" adds them up.
 */

// ------------------------------------------------------------------------------ data

export interface TodayQueue {
  queue_key: string; queue_id: string | null; name: string; offered: number; answered: number; abandoned: number;
  within_target: number; service_level_pct: number | null;
}
interface Board { tz: string; queues: TodayQueue[] }
export interface Member {
  extension_id: string; number: string; name: string; tier_level: number; logged_on: boolean;
  state: string | null; reason_label: string | null; state_since: string | null;
  /** Paused in this queue only (0156); ready in their others. */
  queue_paused?: boolean;
  calls_taken: number; offers: number; missed: number; talk_sec: number; avg_talk_sec: number;
}
export interface Detail {
  tz: string; target_sec: number; queue: { key: string; id: string; name: string };
  today: {
    offered: number; answered: number; abandoned: number; within_target: number; short_abandons: number;
    avg_wait_answered_sec: number; avg_wait_abandoned_sec: number; longest_wait_sec: number;
    calls: number; talk_sec: number; avg_talk_sec: number; longest_talk_sec: number;
  };
  series: Array<{ at: string; answered: number; abandoned: number }>;
  members: Member[];
}

/** A day of the dialler (0135): what the wallboard's dialler widgets show. */
export interface DialDay {
  calls: number; answered: number; humans: number; machines: number; screens: number; unsure: number;
  ai_checks: number; ai_cost: string; currency: string | null;
}

export interface QueueRef { key: string; name: string; slug: string; live: LiveQueue | null; today: TodayQueue | null }

const DETAIL_TYPES = new Set(['agents_ready', 'agents_on_call', 'agents_wrap_up', 'agents_paused', 'calls_today', 'answered',
  'abandoned', 'service_level', 'avg_wait', 'longest_wait_today', 'avg_talk', 'talk_time', 'agents_table', 'agents_by_state', 'hourly']);

export function useWallboardData(tenantId: string | null, layout: WallboardLayout | null) {
  const qc = useQueryClient();
  const { queues: live, skewMs } = useLiveState(['queues']);
  const board = useQuery({
    queryKey: ['wallboard', tenantId, layout?.sl_target_sec ?? 20],
    queryFn: () => api.get<Board>(`/v1/tenants/${tenantId}/wallboard?target_sec=${layout?.sl_target_sec ?? 20}`),
    enabled: !!tenantId, refetchInterval: 15_000, refetchIntervalInBackground: true,
  });
  const queues: QueueRef[] = useMemo(() => {
    const today = new Map((board.data?.queues ?? []).map((q) => [q.queue_key, q]));
    const keys = new Set<string>([...live.map((q) => q.queue), ...(board.data?.queues ?? []).map((q) => q.queue_key)]);
    return [...keys].map((k) => {
      const l = live.find((q) => q.queue === k) ?? null; const t = today.get(k) ?? null;
      const name = t?.name ?? l?.name ?? k;
      return { key: k, name, slug: queueSlug(name), live: l, today: t };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [live, board.data]);

  // The queues whose detail some widget needs: its own, or all of them for "every queue".
  const needed = useMemo(() => {
    const set = new Set<string>();
    for (const w of layout?.widgets ?? []) {
      if (!DETAIL_TYPES.has(w.type)) continue;
      if (w.queue) set.add(w.queue); else for (const q of queues) set.add(q.key);
    }
    return [...set].sort();
  }, [layout, queues]);
  const details = useQueries({
    queries: needed.map((key) => ({
      queryKey: ['wallboard-queue', tenantId, key, layout?.sl_target_sec ?? 20],
      queryFn: () => api.get<Detail>(`/v1/tenants/${tenantId}/wallboard/queue?queue_key=${encodeURIComponent(key)}&target_sec=${layout?.sl_target_sec ?? 20}`),
      enabled: !!tenantId, refetchInterval: 15_000, refetchIntervalInBackground: true,
    })),
  });
  useShiftsChannel(tenantId, () => {
    void qc.invalidateQueries({ queryKey: ['wallboard', tenantId] });
    void qc.invalidateQueries({ queryKey: ['wallboard-queue', tenantId] });
  });
  const detailBy = new Map<string, Detail>();
  details.forEach((d, i) => { if (d.data) detailBy.set(needed[i]!, d.data); });

  // The dialler's day, per campaign a widget shows ('all' for every campaign).
  const dialKeys = useMemo(() => [...new Set((layout?.widgets ?? []).filter((w) => CAMPAIGN_WIDGETS.has(w.type)).map((w) => w.campaign ?? 'all'))].sort(), [layout]);
  const dial = useQueries({
    queries: dialKeys.map((k) => ({
      queryKey: ['wallboard-dialler', tenantId, k],
      queryFn: () => api.get<DialDay>(`/v1/tenants/${tenantId}/wallboard/dialler${k === 'all' ? '' : `?campaign=${k}`}`),
      enabled: !!tenantId, refetchInterval: 15_000, refetchIntervalInBackground: true,
    })),
  });
  const dialBy = new Map<string, DialDay>();
  dial.forEach((d, i) => { if (d.data) dialBy.set(dialKeys[i]!, d.data); });
  return { queues, detailBy, dialBy, tz: board.data?.tz ?? null, skewMs, error: board.error ?? details.find((d) => d.error)?.error ?? null };
}

/** One queue's detail, or every queue's added up. */
export function detailFor(queue: string | null, queues: QueueRef[], detailBy: Map<string, Detail>): Detail | null {
  if (queue) return detailBy.get(queue) ?? null;
  const all = queues.map((q) => detailBy.get(q.key)).filter((d): d is Detail => !!d);
  if (all.length === 0) return null;
  const sum = (f: (d: Detail) => number) => all.reduce((n, d) => n + f(d), 0);
  const answered = sum((d) => d.today.answered); const abandoned = sum((d) => d.today.abandoned); const calls = sum((d) => d.today.calls);
  const members = new Map<string, Member>();
  for (const d of all) for (const m of d.members) {
    const prev = members.get(m.extension_id);
    if (!prev) { members.set(m.extension_id, { ...m }); continue; }
    prev.calls_taken += m.calls_taken; prev.offers += m.offers; prev.missed += m.missed; prev.talk_sec += m.talk_sec;
    prev.avg_talk_sec = prev.calls_taken ? Math.round(prev.talk_sec / prev.calls_taken) : 0;
  }
  return {
    tz: all[0]!.tz, target_sec: all[0]!.target_sec, queue: { key: 'all', id: 'all', name: 'All queues' },
    today: {
      offered: sum((d) => d.today.offered), answered, abandoned,
      within_target: sum((d) => d.today.within_target), short_abandons: sum((d) => d.today.short_abandons),
      avg_wait_answered_sec: answered ? Math.round(sum((d) => d.today.avg_wait_answered_sec * d.today.answered) / answered) : 0,
      avg_wait_abandoned_sec: abandoned ? Math.round(sum((d) => d.today.avg_wait_abandoned_sec * d.today.abandoned) / abandoned) : 0,
      longest_wait_sec: Math.max(...all.map((d) => d.today.longest_wait_sec)),
      calls, talk_sec: sum((d) => d.today.talk_sec), avg_talk_sec: calls ? Math.round(sum((d) => d.today.talk_sec) / calls) : 0,
      longest_talk_sec: Math.max(...all.map((d) => d.today.longest_talk_sec)),
    },
    series: all[0]!.series.map((h, i) => ({ at: h.at, answered: sum((d) => d.series[i]?.answered ?? 0), abandoned: sum((d) => d.series[i]?.abandoned ?? 0) })),
    members: [...members.values()].sort((a, b) => Number(b.logged_on) - Number(a.logged_on) || a.name.localeCompare(b.name)),
  };
}

function liveFor(queue: string | null, queues: QueueRef[]): LiveQueue | null {
  if (queue) return queues.find((q) => q.key === queue)?.live ?? null;
  const ls = queues.map((q) => q.live).filter((l): l is LiveQueue => !!l);
  if (ls.length === 0) return null;
  const since = ls.map((l) => l.longest_waiting_since).filter((x): x is number => x != null);
  return {
    queue: 'all', name: 'All queues', domain: '', waiting: ls.reduce((n, l) => n + l.waiting, 0),
    agents_total: ls.reduce((n, l) => n + l.agents_total, 0), agents_available: ls.reduce((n, l) => n + l.agents_available, 0),
    agents_on_call: ls.reduce((n, l) => n + l.agents_on_call, 0), calls_answered: ls.reduce((n, l) => n + l.calls_answered, 0),
    longest_wait_sec: Math.max(...ls.map((l) => l.longest_wait_sec)), longest_waiting_since: since.length ? Math.min(...since) : null,
  };
}

// ------------------------------------------------------------------------------ templates

let n = 0;
const wid = () => `w${Date.now().toString(36)}${(n++).toString(36)}`;
export const widget = (type: WallboardWidget['type'], queue: string | null, over: Partial<WallboardWidget> = {}): WallboardWidget =>
  ({ id: wid(), type, queue: WALLBOARD_WIDGETS[type].queue ? queue : null, campaign: null, span: WALLBOARD_WIDGETS[type].span, rows: WALLBOARD_WIDGETS[type].rows, ...over });
/** A widget's height, for boards saved before heights existed. */
export const rowsOf = (w: WallboardWidget): number => w.rows ?? WALLBOARD_WIDGETS[w.type].rows;

const THRESHOLDS = { sl_target_sec: 20, wait_amber_sec: 30, wait_red_sec: 120, waiting_amber: 3, waiting_red: 6, sl_red_pct: 80 };

/** Everything that matters about one queue (owner, 26 September 2026). */
export const singleQueueTemplate = (key: string): WallboardLayout => ({
  ...THRESHOLDS,
  widgets: [
    widget('waiting', key), widget('longest_wait', key), widget('agents_ready', key), widget('agents_paused', key),
    widget('calls_today', key), widget('answered', key), widget('abandoned', key), widget('service_level', key),
    widget('avg_wait', key), widget('longest_wait_today', key), widget('avg_talk', key), widget('talk_time', key),
    widget('agents_table', key, { span: 3 }), widget('hourly', key, { span: 1 }),
  ],
});

/** Every queue at once. */
export const allQueuesTemplate = (): WallboardLayout => ({
  ...THRESHOLDS,
  widgets: [
    widget('waiting', null), widget('longest_wait', null), widget('calls_today', null), widget('service_level', null),
    widget('queue_tiles', null), widget('agents_by_state', null),
  ],
});

/**
 * Boards to start from (owner, 26 September 2026: "default wall board templates to choose from and
 * edit"). Kept apart on purpose: what is happening on the floor now is a different board from how
 * the queue is doing today.
 */
export const TEMPLATES: Array<{ key: string; name: string; hint: string; queue: boolean; build: (q: string | null) => WallboardLayout }> = [
  { key: 'floor', name: 'Queue floor, live', hint: 'Who is waiting, for how long, and every agent’s state right now.', queue: true,
    build: (q) => ({ ...THRESHOLDS, widgets: [
      widget('waiting', q, { rows: 2 }), widget('longest_wait', q, { rows: 2 }), widget('agents_ready', q), widget('agents_paused', q),
      widget('agents_on_call', q), widget('agents_wrap_up', q), widget('agents_by_state', q, { rows: 2 }),
    ] }) },
  { key: 'performance', name: 'Queue performance today', hint: 'Calls, answered, abandoned, service level, waits and talk time since midnight.', queue: true,
    build: (q) => ({ ...THRESHOLDS, widgets: [
      widget('calls_today', q), widget('answered', q), widget('abandoned', q), widget('service_level', q),
      widget('avg_wait', q), widget('longest_wait_today', q), widget('avg_talk', q), widget('talk_time', q),
      widget('hourly', q, { span: 4, rows: 2 }),
    ] }) },
  { key: 'everything', name: 'Queue, everything', hint: 'Live and today’s figures together, with the agents table.', queue: true,
    build: (q) => singleQueueTemplate(q!) },
  { key: 'agents', name: 'Agent status', hint: 'Every agent by state, and the table of their calls today.', queue: true,
    build: (q) => ({ ...THRESHOLDS, widgets: [
      widget('agents_ready', q), widget('agents_on_call', q), widget('agents_wrap_up', q), widget('agents_paused', q),
      widget('agents_by_state', q, { rows: 2 }), widget('agents_table', q, { span: 4, rows: 3 }),
    ] }) },
  { key: 'all-queues', name: 'All queues overview', hint: 'A tile per queue, with the totals across them.', queue: false,
    build: () => allQueuesTemplate() },
  { key: 'dialler', name: 'Dialler today', hint: 'Calls placed, people reached, machines and call screens, and the AI checks with their cost.', queue: false,
    build: () => ({ ...THRESHOLDS, widgets: [
      widget('dial_calls', null), widget('amd_humans', null), widget('amd_machines', null), widget('amd_screens', null),
      widget('amd_ai_checks', null),
    ] }) },
  { key: 'blank', name: 'Blank', hint: 'Start empty and add widgets from the list.', queue: false,
    build: () => ({ ...THRESHOLDS, widgets: [] }) },
];

// ------------------------------------------------------------------------------ drawing

export interface DrawContext {
  queues: QueueRef[]; detailBy: Map<string, Detail>; now: number; big: number; layout: WallboardLayout; tz: string | null;
  dialBy?: Map<string, DialDay> | undefined;
  /** Campaign names, for dialler widgets' titles. */
  campaigns?: Array<{ id: string; name: string }> | undefined;
}

export function WidgetView({ w, ctx }: { w: WallboardWidget; ctx: DrawContext }) {
  const { layout: L, big, now } = ctx;
  const toneWait = (s: number): Tone => (s >= L.wait_red_sec ? 'danger' : s >= L.wait_amber_sec ? 'warn' : 'ok');
  const toneWaiting = (x: number): Tone => (x >= L.waiting_red ? 'danger' : x >= L.waiting_amber ? 'warn' : 'ok');
  const toneSl = (p: number | null): Tone => (p === null ? 'ok' : p < L.sl_red_pct - 10 ? 'danger' : p < L.sl_red_pct ? 'warn' : 'ok');
  const qname = w.queue ? ctx.queues.find((q) => q.key === w.queue)?.name ?? 'A queue no longer here' : 'All queues';
  const title = w.title || WALLBOARD_WIDGETS[w.type].label;
  const isDial = CAMPAIGN_WIDGETS.has(w.type);
  const cname = w.campaign ? ctx.campaigns?.find((x) => x.id === w.campaign)?.name ?? 'A campaign' : 'All campaigns';
  const day = isDial ? ctx.dialBy?.get(w.campaign ?? 'all') : undefined;
  const live = liveFor(w.queue, ctx.queues);
  const d = DETAIL_TYPES.has(w.type) ? detailFor(w.queue, ctx.queues, ctx.detailBy) : null;
  const t = d?.today;
  const members = d?.members ?? [];
  const inState = (s: string) => members.filter((m) => m.logged_on && (m.state ?? 'ready') === s).length;
  const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 1000) / 10 : null);
  const waitNow = !live ? 0 : live.longest_waiting_since == null ? (live.waiting > 0 ? live.longest_wait_sec : 0) : (now - live.longest_waiting_since) / 1000;

  const look = LOOK[w.type] ?? { hue: 0 as const, icon: 'dashboard' as IconName };
  // Only amber and red override the tile's own colour: those are what must be seen across a room.
  const Tile = ({ value, tone, sub, size = 38 }: { value: string; tone?: Tone | undefined; sub?: string | undefined; size?: number }) => (
    <div className="arc-wb-tile" data-hue={look.hue} data-tone={tone && tone !== 'ok' ? tone : undefined}
      style={{ padding: `${14 * big}px ${14 * big}px ${12 * big}px` }}>
      <div className="arc-wb-tile-head" style={{ fontSize: 12.5 * big }}>
        <Icon name={look.icon} size={Math.round(15 * big)} />
        <span className="arc-truncate">{title}{WALLBOARD_WIDGETS[w.type].queue ? ` · ${qname}` : isDial ? ` · ${cname}` : ''}</span>
      </div>
      {/* Centred in a taller tile, rather than left at the top of an empty box. */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: rowsOf(w) > 1 ? 'center' : 'flex-start' }}>
        <div className="arc-wb-value" style={{ fontSize: size * big * (1 + 0.5 * (rowsOf(w) - 1)), marginTop: 4 * big }}>{value}</div>
        {sub && <div className="arc-subtle arc-num" style={{ fontSize: 11.5 * big * (rowsOf(w) > 1 ? 1.2 : 1) }}>{sub}</div>}
      </div>
    </div>
  );
  const sl = t ? pct(t.within_target, t.offered) : null;

  switch (w.type) {
    case 'heading':
      return <div style={{ fontSize: 22 * big, fontWeight: 650, padding: '4px 2px' }}>{w.title || 'Heading'}</div>;
    case 'clock':
      return <Tile value={new Date(now).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', ...(ctx.tz ? { timeZone: ctx.tz } : {}) })} />;
    case 'waiting': return <Tile value={String(live?.waiting ?? 0)} tone={toneWaiting(live?.waiting ?? 0)} size={50} />;
    case 'longest_wait': return <Tile value={mmss(waitNow)} tone={toneWait(waitNow)} size={50} />;
    case 'agents_ready': return <Tile value={String(inState('ready'))} sub={live ? `${live.agents_available} ${live.agents_available === 1 ? 'phone' : 'phones'} free in the queue` : undefined} />;
    case 'agents_on_call': return <Tile value={String(inState('on_call'))} sub={live ? `${live.agents_on_call} on queue calls now` : undefined} />;
    case 'agents_wrap_up': return <Tile value={String(inState('wrap_up'))} />;
    case 'agents_paused': return <Tile value={String(inState('not_ready'))} tone={inState('not_ready') ? 'warn' : undefined}
      sub={`${members.filter((m) => !m.logged_on).length} of ${members.length} logged off`} />;
    case 'calls_today': return <Tile value={String(t?.offered ?? 0)} />;
    case 'answered': return <Tile value={String(t?.answered ?? 0)} sub={t?.offered ? `${pct(t.answered, t.offered)}% of calls` : undefined} />;
    case 'abandoned': return <Tile value={String(t?.abandoned ?? 0)} tone={t?.abandoned ? 'warn' : undefined}
      sub={t?.offered ? `${pct(t.abandoned, t.offered)}% · ${t.short_abandons} within 5 s` : undefined} />;
    case 'service_level': return <Tile value={sl === null ? '—' : `${sl}%`} tone={toneSl(sl)} sub={`answered within ${L.sl_target_sec} s`} />;
    case 'avg_wait': return <Tile value={mmss(t?.avg_wait_answered_sec ?? 0)} sub={t ? `abandoned after ${mmss(t.avg_wait_abandoned_sec)} on average` : undefined} />;
    case 'longest_wait_today': return <Tile value={mmss(t?.longest_wait_sec ?? 0)} tone={toneWait(t?.longest_wait_sec ?? 0)} />;
    case 'avg_talk': return <Tile value={mmss(t?.avg_talk_sec ?? 0)} sub={t ? `longest ${mmss(t.longest_talk_sec)}` : undefined} />;
    case 'talk_time': return <Tile value={mmss(t?.talk_sec ?? 0)} sub={t ? `${t.calls} calls handled` : undefined} />;

    case 'dial_calls': return <Tile value={String(day?.calls ?? 0)} sub={day ? `${day.answered} answered` : undefined} />;
    case 'amd_humans': return <Tile value={String(day?.humans ?? 0)} sub={day?.answered ? `${pct(day.humans, day.answered)}% of answered calls` : undefined} />;
    case 'amd_machines': return <Tile value={String(day?.machines ?? 0)} sub={day?.answered ? `${pct(day.machines, day.answered)}% of answered calls` : undefined} />;
    case 'amd_screens': return <Tile value={String(day?.screens ?? 0)} sub="phones that asked who was calling" />;
    case 'amd_ai_checks': return <Tile value={String(day?.ai_checks ?? 0)}
      sub={day ? (Number(day.ai_cost) > 0 ? `${formatMoney(day.ai_cost, day.currency ?? 'ZAR')} today` : 'no charge today') : undefined} />;

    case 'agents_table':
      return (
        <div className="arc-card" style={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <div className="arc-subtle" style={{ fontSize: 12 * big, padding: '10px 12px 0' }}>{title} · {qname}</div>
          <div className="arc-table-wrap" style={{ flex: 1, overflow: 'auto' }}>
            <table className="arc-table" style={{ fontSize: 13 * big }}>
              <thead><tr><th>Agent</th><th>State</th><th className="arc-td-right">For</th><th className="arc-td-right">Calls</th>
                <th className="arc-td-right">Talk</th><th className="arc-td-right">Avg</th><th className="arc-td-right">Missed</th></tr></thead>
              <tbody>
                {members.map((m) => {
                  const s = m.logged_on ? (m.state ?? 'ready') : null;
                  const st = s ? AGENT_STATE[s as keyof typeof AGENT_STATE] : null;
                  return (
                    <tr key={m.extension_id} style={m.logged_on ? undefined : { opacity: 0.45 }}>
                      <td><span style={{ fontWeight: 550 }}>{m.name}</span> <span className="arc-subtle arc-mono">{m.number}</span></td>
                      <td>{st ? <span className="arc-wb-pill" data-state={s}>{st.label}{m.reason_label && s === 'not_ready' ? ` · ${m.reason_label}` : ''}{m.queue_paused && s === 'not_ready' ? ' · this queue only' : ''}</span>
                        : <span className="arc-subtle">Logged off</span>}</td>
                      <td className="arc-td-right arc-num">{m.logged_on && m.state_since ? mmss((now - Date.parse(m.state_since)) / 1000) : ''}</td>
                      <td className="arc-td-right arc-num">{m.calls_taken}</td>
                      <td className="arc-td-right arc-num">{mmss(m.talk_sec)}</td>
                      <td className="arc-td-right arc-num">{m.calls_taken ? mmss(m.avg_talk_sec) : '—'}</td>
                      <td className="arc-td-right arc-num" style={{ color: m.missed ? TONE.warn : undefined }}>{m.missed}</td>
                    </tr>
                  );
                })}
                {members.length === 0 && <tr><td colSpan={7} className="arc-subtle">{d ? 'Nobody is in this queue.' : '…'}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      );

    case 'agents_by_state':
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, height: '100%', overflow: 'auto' }}>
          {(Object.keys(AGENT_STATE) as Array<keyof typeof AGENT_STATE>).map((s) => {
            const list = members.filter((m) => m.logged_on && (m.state ?? 'ready') === s);
            return (
              <div key={s} className="arc-wb-state" data-state={s}>
                <div className="arc-wb-state-head">
                  <strong style={{ fontSize: 14 * big, flex: 1 }}>{AGENT_STATE[s].label}</strong>
                  <span className="arc-num" style={{ fontSize: 22 * big, fontWeight: 700 }}>{list.length}</span>
                </div>
                <div className="arc-stack" style={{ gap: 4, padding: 12 * big }}>
                {list.length === 0 ? <span className="arc-subtle" style={{ fontSize: 12 * big }}>Nobody</span> : list.map((m) => (
                  <div key={m.extension_id} className="arc-row" style={{ gap: 8, fontSize: 13 * big }}>
                    <span style={{ flex: 1 }} className="arc-truncate">{m.name}{m.reason_label && s === 'not_ready' ? <span className="arc-subtle"> · {m.reason_label}{m.queue_paused ? ' · this queue only' : ''}</span> : null}</span>
                    <span className="arc-num arc-subtle">{m.state_since ? mmss((now - Date.parse(m.state_since)) / 1000) : ''}</span>
                  </div>
                ))}
                </div>
              </div>
            );
          })}
        </div>
      );

    case 'queue_tiles':
      return (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${260 * big}px, 1fr))`, gap: 12, height: '100%', overflow: 'auto', alignContent: 'start' }}>
          {ctx.queues.map((q) => {
            const l = q.live;
            const wq = !l ? 0 : l.longest_waiting_since == null ? (l.waiting > 0 ? l.longest_wait_sec : 0) : (now - l.longest_waiting_since) / 1000;
            const tone = worst(toneWait(wq), toneWaiting(l?.waiting ?? 0), toneSl(q.today?.service_level_pct ?? null));
            return (
              <div key={q.key} className="arc-wb-queue" style={{ padding: 14 * big, borderTop: `3px solid ${TONE[tone]}` }}>
                <div className="arc-row" style={{ gap: 8 }}>
                  <strong style={{ fontSize: 16 * big, flex: 1 }}>{q.name}</strong>
                  <span className="arc-subtle" style={{ fontSize: 12 * big }}>{l ? `${l.agents_available} free · ${l.agents_on_call} on calls` : ''}</span>
                </div>
                <div className="arc-row" style={{ gap: 24, marginTop: 6 }}>
                  <div><div className="arc-subtle" style={{ fontSize: 11 * big }}>Waiting</div>
                    <div className="arc-num" style={{ fontSize: 38 * big, fontWeight: 700, lineHeight: 1.1, color: l?.waiting ? TONE[toneWaiting(l.waiting)] : undefined }}>{l?.waiting ?? 0}</div></div>
                  <div><div className="arc-subtle" style={{ fontSize: 11 * big }}>Longest wait</div>
                    <div className="arc-num" style={{ fontSize: 26 * big, fontWeight: 650, lineHeight: 1.4, color: wq > 0 ? TONE[toneWait(wq)] : undefined }}>{mmss(wq)}</div></div>
                </div>
                <div className="arc-row arc-num" style={{ gap: 12, marginTop: 8, fontSize: 13 * big, flexWrap: 'wrap' }}>
                  <span><span className="arc-subtle">Calls</span> {q.today?.offered ?? 0}</span>
                  <span><span className="arc-subtle">Answered</span> {q.today?.answered ?? 0}</span>
                  <span style={{ color: q.today?.abandoned ? TONE.warn : undefined }}><span className="arc-subtle">Abandoned</span> {q.today?.abandoned ?? 0}</span>
                  <span style={{ color: TONE[toneSl(q.today?.service_level_pct ?? null)] }}><span className="arc-subtle">SL</span> {q.today?.service_level_pct == null ? '—' : `${q.today.service_level_pct}%`}</span>
                </div>
              </div>
            );
          })}
        </div>
      );

    case 'hourly': {
      const hours = (d?.series ?? []).filter((h) => Date.parse(h.at) <= now);
      const firstBusy = hours.findIndex((h) => h.answered + h.abandoned > 0);
      const shown = hours.slice(Math.max(0, Math.min(firstBusy < 0 ? 7 : firstBusy, 7)));
      const peak = Math.max(1, ...shown.map((h) => h.answered + h.abandoned));
      const label = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', ...(ctx.tz ? { timeZone: ctx.tz } : {}) });
      return (
        <div className="arc-card" style={{ padding: 14 * big, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <div className="arc-row" style={{ gap: 10, marginBottom: 10, fontSize: 12 * big }}>
            <span className="arc-subtle" style={{ flex: 1 }}>{title} · {qname}</span>
            <span className="arc-row" style={{ gap: 4 }}><span style={{ width: 9, height: 9, background: TONE.ok, borderRadius: 2 }} />answered</span>
            <span className="arc-row" style={{ gap: 4 }}><span style={{ width: 9, height: 9, background: TONE.warn, borderRadius: 2 }} />abandoned</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, flex: 1, minHeight: 60 }}>
            {shown.map((h) => (
              <div key={h.at} title={`${label(h.at)}:00 · ${h.answered} answered, ${h.abandoned} abandoned`}
                style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <div style={{ height: `${(h.abandoned / peak) * 100}%`, background: TONE.warn }} />
                <div style={{ height: `${(h.answered / peak) * 100}%`, background: TONE.ok }} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
            {shown.map((h, i) => <span key={h.at} className="arc-subtle arc-num" style={{ flex: 1, textAlign: 'center', fontSize: 10 * big }}>{i % 2 === 0 ? label(h.at) : ''}</span>)}
          </div>
        </div>
      );
    }
  }
}

/** Each figure's colour and glyph: the same hue for the same kind of thing on every board. */
const LOOK: Partial<Record<WallboardWidget['type'], { hue: 0 | 1 | 2 | 3 | 4 | 5; icon: IconName }>> = {
  clock: { hue: 0, icon: 'clock' },
  waiting: { hue: 1, icon: 'queue' },
  longest_wait: { hue: 5, icon: 'clock' },
  agents_ready: { hue: 2, icon: 'check' },
  agents_on_call: { hue: 1, icon: 'headset' },
  agents_wrap_up: { hue: 4, icon: 'edit' },
  agents_paused: { hue: 3, icon: 'hold' },
  calls_today: { hue: 1, icon: 'callIn' },
  answered: { hue: 2, icon: 'check' },
  abandoned: { hue: 5, icon: 'close' },
  service_level: { hue: 4, icon: 'activity' },
  avg_wait: { hue: 3, icon: 'clock' },
  longest_wait_today: { hue: 5, icon: 'warning' },
  avg_talk: { hue: 4, icon: 'phone' },
  talk_time: { hue: 2, icon: 'headset' },
  dial_calls: { hue: 1, icon: 'callOut' },
  amd_humans: { hue: 2, icon: 'check' },
  amd_machines: { hue: 3, icon: 'mail' },
  amd_screens: { hue: 4, icon: 'phone' },
  amd_ai_checks: { hue: 5, icon: 'activity' },
};

const AGENT_STATE = {
  ready: { label: 'Ready', tone: 'ok' as Tone | 'muted' },
  on_call: { label: 'On a call', tone: 'warn' as Tone | 'muted' },
  wrap_up: { label: 'Wrap-up', tone: 'muted' as Tone | 'muted' },
  not_ready: { label: 'Paused', tone: 'danger' as Tone | 'muted' },
} as const;

/** The four-column grid a board is laid out on; one column on a phone. */
export function BoardGrid({ widgets, ctx, wrap }: {
  widgets: WallboardWidget[]; ctx: DrawContext;
  wrap?: (w: WallboardWidget, i: number, body: React.ReactNode) => React.ReactNode;
}) {
  return (
    <div className="arc-wallboard-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gridAutoRows: `${(wrap ? 176 : 124) * ctx.big}px`, gap: 12 * ctx.big, gridAutoFlow: 'row dense' }}>
      {widgets.map((w, i) => {
        const body = <WidgetView w={w} ctx={ctx} />;
        return (
          <div key={w.id} className="arc-wallboard-cell" style={{ gridColumn: `span ${w.span}`, gridRow: `span ${rowsOf(w)}`, minWidth: 0, minHeight: 0 }}>
            {wrap ? wrap(w, i, body) : body}
          </div>
        );
      })}
    </div>
  );
}
